import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { ExperimentsPlaceholder } from '@candu-ai/experiments/ui';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { useEventing } from 'commons/eventing';
import { useOutsideClick } from 'commons/hooks';
import { CloseButton } from 'craft/components/CloseButton';
import Node from 'craft/components/Node';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { VIEWPORT_CONTENT_ID } from 'craft/containers/Viewport/constants';
import { useNodeContext, useResponsiveCss, useResponsiveProps } from 'craft/hooks';
import { useModalPositioning } from 'craft/hooks/useModalPositioning';
import { useModalSizing } from 'craft/hooks/useModalSizing';
import { setScrollableBodyDom, useToolboxState, useViewportBreakpoint } from 'craft/state';
import { MediaBreakpoint, Resolver, ToolboxComponentSections } from 'craft/types';
import { hasDescendants } from 'craft/utils';

import { Placeholder } from './Placeholder';
import { OverlayBackdrop } from './toolbox/OverlayBackdrop';
import { OverlayDismiss } from './toolbox/OverlayDismiss';
import { OverlayPosition } from './toolbox/OverlayPosition';
import { useModalPosition } from './useModalPosition';

const positionToOffset = (pos?: string) => {
  const castToOffset: Record<string, '0%' | '50%' | '100%'> = {
    left: '0%',
    center: '50%',
    right: '100%',
    top: '0%',
    bottom: '100%',
  };
  if (pos && Object.keys(castToOffset).includes(pos)) {
    return castToOffset[pos];
  }
  return '0%';
};

const mapDimensionsToProps = (
  css: Record<string, unknown>,
  props: Record<string, string>,
  setProps: (props: Record<string, any>) => void,
) => {
  if (!!css.width && !props.width) {
    setProps({ width: css.width });
  }
  if (!!css.minWidth && !props.minWidth) {
    setProps({ minWidth: css.minWidth });
  }
  if (!!css.maxWidth && !props.maxWidth) {
    setProps({ maxWidth: css.maxWidth });
  }
  if (!!css.height && !props.height) {
    setProps({ height: css.height });
  }
  if (!!css.minHeight && !props.minHeight) {
    setProps({ minHeight: css.minHeight });
  }
  if (!!css.maxHeight && !props.maxHeight) {
    setProps({ maxHeight: css.maxHeight });
  }
};

export const MODAL_BODY_ID = 'modal-body-container';
export const MODAL_BACKDROP_ID = 'modal-backdrop';

export const Modal: Resolver = ({
  children,
  isDismissible,
  hideBackdrop,
  variant = 'base',
}: any) => {
  const viewportBreakpoint = useViewportBreakpoint();
  const { responsiveProps, setResponsiveProps } = useResponsiveProps([CRAFT_ROOT_ID]) ?? {};
  const { responsiveCss, setResponsiveCss } = useResponsiveCss([CRAFT_ROOT_ID]) ?? {};
  const { id } = useNodeContext();
  const hasChildren = hasDescendants(children);
  const params = useParams();
  const { position } = useModalPosition();
  const isExperiment = !!params?.variantId;
  const bodyRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();

  const { track } = useEventing();
  const { minimizeToolboxOnSnapshotClick } = useToolboxState();

  const placeholderComponent = isExperiment ? (
    <ExperimentsPlaceholder />
  ) : (
    <Placeholder data-cy="placeholder" />
  );

  // extract props that shouldn't be passed to the dom
  const {
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    stateId,
    horizontalPosition,
    verticalPosition,
    zIndex,
    ...modalProps
  } = responsiveProps;

  const offsetTop: string = positionToOffset(position.vertical);
  const offsetLeft: string = positionToOffset(position.horizontal);

  const wrapperClassName = toCssSelector('modalV2', {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  const backdropClass = toCssSelector('modalV2', {
    output: 'html',
    className: 'backdrop',
  });

  const modalClassName = toCssSelector('modalV2', {
    className: 'container',
    output: 'html',
  });

  // the spacing properties
  const {
    top,
    left,
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
    transform,
    ...modalStyles
  } = useModalPositioning({
    offsetTop,
    offsetLeft,
    // TODO: we can remove responsiveCSS
    // after we migrate everyone to responsive props
    containerCss: { ...responsiveCss, ...responsiveProps },
  });

  // the sizing properties for the editor
  const { sizes } = useModalSizing({
    styles: {
      marginLeft,
      marginRight,
      marginTop,
      marginBottom,
    },
    props: responsiveProps,
  });

  useEffect(() => {
    if (viewportBreakpoint !== MediaBreakpoint.LG) {
      return;
    }
    // only run on first render
    mapDimensionsToProps(modalStyles, responsiveProps, setResponsiveProps);
  }, [viewportBreakpoint]);

  useEffect(() => {
    setResponsiveCss({
      ...sizes,
      left,
      top,
      transform,
    });
  }, [sizes, left, top]);

  useEffect(() => {
    // save document root's scrollable body ref to the store
    dispatch(setScrollableBodyDom(bodyRef));
  }, []);

  // @ts-ignore
  useOutsideClick(containerRef?.current, (e) => {
    // @ts-ignore
    const targetId = e?.target?.id;

    if (targetId !== VIEWPORT_CONTENT_ID && targetId !== MODAL_BACKDROP_ID) {
      // only consider an outside click when it's triggered from inside the canvas
      return;
    }

    if (minimizeToolboxOnSnapshotClick()) {
      track('editor.dockedToolbox.snapshotClose');
    }
  });

  return (
    <div
      id={`cu-${id}`}
      className={wrapperClassName}
      style={{
        // ensure modal is correctly rendered in the editor
        position: 'initial',
        display: 'none',
      }}
    >
      {!hideBackdrop && (
        <div
          id={MODAL_BACKDROP_ID}
          data-cy="backdrop"
          className={backdropClass}
          style={{
            // this is for editor only
            zIndex: 0,
            position: 'absolute',
            width: `100%`,
            top: 0,
            // end for editor only
          }}
        />
      )}
      <Node
        innerRef={(dom: any) => {
          containerRef.current = dom;
        }}
        is="div"
        data-cy="node-modal-container-wrapper"
        className={modalClassName}
        style={{
          // this is for editor only
          zIndex: 0,
          position: 'absolute',
          // end for editor only
          // overrides for outer container
          margin: 0,
          marginLeft: 0,
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          padding: 0,
          paddingLeft: 0,
          paddingTop: 0,
          paddingRight: 0,
          paddingBottom: 0,
          overflow: 'visible',
          display: 'initial',
          borderWidth: 0,
          borderStyle: 'none',
          borderColor: 'unset',
          backgroundColor: 'unset',
          backgroundImage: 'none',
        }}
      >
        {isDismissible && <CloseButton />}
        <div
          id={MODAL_BODY_ID}
          ref={(dom: any) => {
            bodyRef.current = dom;
          }}
          className={modalClassName}
          data-cy="node-modal-container"
          style={{
            ...modalStyles,
            width: '100%',
            height: '100%',
            minWidth: '100%',
            minHeight: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            margin: 0,
            position: 'initial',
            top: 0,
            left: 0,
            transform: 'none',
            boxSizing: 'border-box',
          }}
          {...modalProps}
        >
          {hasChildren ? children : placeholderComponent}
        </div>
      </Node>
    </div>
  );
};

Modal.craft = {
  name: BuiltInComponents.Modal,
  displayName: 'Overlay',
  isCanvas: true,
  rules: {
    canDelete: () => false,
    canDrag: () => false,
    canDrop: () => false,
  },
};

Modal.toolbox = {
  variantKey: 'modalV2_base',
  component: [OverlayPosition, OverlayDismiss, OverlayBackdrop],
  responsiveComponent: [OverlayPosition],
  schema: {
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['none', 'inline-block', 'block'],
    },
    [ToolboxComponentSections.Size]: {
      hasOverflow: true,
      hasAutoSize: true,
    },
  },
};

Modal.resize = {
  shouldDisableHandlers: true,
};
